import React, { useState, useCallback } from 'react';
import { Input, Button, Space, Card } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import type { QueryParams } from '../../../services/p9306Api';

interface FilterPanelProps {
  loading: boolean;
  onQuery: (params: QueryParams) => void;
  onReset: () => void;
  onLookupDept: (code: string) => Promise<string>;
}

export const FilterPanel = React.memo(({ loading, onQuery, onReset, onLookupDept }: FilterPanelProps) => {
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [deptFromName, setDeptFromName] = useState('');
  const [deptToName, setDeptToName] = useState('');

  const handleDeptFromBlur = useCallback(async () => {
    const trimmed = deptFrom.trim().toUpperCase();
    setDeptFrom(trimmed);
    if (trimmed) {
      // BR-V01: Auto-fill deptTo from deptFrom
      const autoTo = (trimmed + 'zzzz').substring(0, 4).toUpperCase();
      setDeptTo(autoTo);
      const name = await onLookupDept(trimmed);
      setDeptFromName(name);
      const toName = await onLookupDept(autoTo);
      setDeptToName(toName);
    } else {
      setDeptTo('');
      setDeptFromName('');
      setDeptToName('');
    }
  }, [deptFrom, onLookupDept]);

  const handleDeptToBlur = useCallback(async () => {
    const trimmed = deptTo.trim().toUpperCase();
    setDeptTo(trimmed);
    if (trimmed) {
      const name = await onLookupDept(trimmed);
      setDeptToName(name);
    } else {
      setDeptToName('');
    }
  }, [deptTo, onLookupDept]);

  const handleQuery = useCallback(() => {
    onQuery({
      deptFrom: deptFrom.trim().toUpperCase() || undefined,
      deptTo: deptTo.trim().toUpperCase() || undefined,
    });
  }, [deptFrom, deptTo, onQuery]);

  const handleReset = useCallback(() => {
    setDeptFrom('');
    setDeptTo('');
    setDeptFromName('');
    setDeptToName('');
    onReset();
  }, [onReset]);

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space wrap size="middle">
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
            部門代號 [從]
          </label>
          <Input
            value={deptFrom}
            onChange={e => setDeptFrom(e.target.value)}
            onBlur={handleDeptFromBlur}
            placeholder="起始代號"
            style={{ width: 140 }}
            maxLength={4}
            allowClear
          />
          {deptFromName && (
            <div style={{ fontSize: 11, color: '#1890ff', marginTop: 2 }}>{deptFromName}</div>
          )}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
            [至]
          </label>
          <Input
            value={deptTo}
            onChange={e => setDeptTo(e.target.value)}
            onBlur={handleDeptToBlur}
            placeholder="結束代號"
            style={{ width: 140 }}
            maxLength={4}
            allowClear
          />
          {deptToName && (
            <div style={{ fontSize: 11, color: '#1890ff', marginTop: 2 }}>{deptToName}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
              查詢
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleReset}>
              清除
            </Button>
          </Space>
        </div>
      </Space>
    </Card>
  );
});

FilterPanel.displayName = 'FilterPanel';
